import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { SupportAgent } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { IraSearchHistoryService } from '../../../../../services/ira-search-history.service';
import { IraSearchHistoryTypes } from '../../../../../types';
import { RESPONSE_CODES } from '../../../../../constants/responseCodes.constants';

// Where reports go (pom-2-api supportInbox()); used when the email fails.
const SUPPORT_EMAIL = 'support@pomsafe.com';

// Mirrors pom-2-api's MIN/MAX_FLAG_COMMENT_LENGTH (iraFlagCommentGuard).
const MIN_LENGTH = 3;
const MAX_LENGTH = 1000;

const DETAIL_LABELS: Record<IraSearchHistoryTypes.PatientDetailKind, string> = {
    name: "the patient's name",
    dateOfBirth: "the patient's date of birth",
    date: 'dates',
    address: 'street addresses',
    ssn: 'Social Security numbers',
    phone: 'phone numbers',
    email: 'email addresses',
};

const listOf = (items: string[]) =>
    items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

interface Props {
    open: boolean;
    target: IraSearchHistoryTypes.FlagTarget | null;
    onClose(): void;
    /** Called with the report's reference (IRA-XXXXXXXX) once it is sent. */
    onFlagged?(reference: string): void;
}

// "Report to POM Support" (ClickUp 868mkwmk5; "flag" in the API). Replaces
// the "this doesn't look right" emails that carried patient details: the
// comment stays in the app and POM Support gets an email with a reference
// only. It goes to POM, not to anyone in the user's organization, and the
// copy says so. The API refuses comments with patient details and says which
// kinds it found.
export const FlagResultDialog: React.FC<Props> = ({ open, target, onClose, onFlagged }) => {
    const [comment, setComment] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            setComment('');
            setError(null);
        }
    }, [open]);

    // Same normalization the API applies before its length check (collapse
    // whitespace, trim), so the counter and the Send button agree with it.
    const normalizedComment = comment.replace(/\s+/g, ' ').trim();
    const trimmedLength = normalizedComment.length;

    const submit = async () => {
        if (!target || isSaving) return;
        setIsSaving(true);
        setError(null);
        try {
            const { reference, emailSent } = await IraSearchHistoryService.createFlag(target, normalizedComment);
            if (emailSent) {
                toast(`Sent to POM Support. Your reference is ${reference}.`, { type: 'success', autoClose: 5000 });
            } else {
                // The report is saved but the support email failed, and nothing
                // resends it. Ask the user to email support with the reference
                // (no patient details) so the report isn't missed. Stays up
                // until dismissed.
                const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`IRA report ${reference}`)}`;
                toast(
                    <span>
                        {`Your report was saved as ${reference}, but the email to POM Support didn't go out. Please email `}
                        <a href={mailto}>{SUPPORT_EMAIL}</a>
                        {` and quote ${reference}. Don't include patient details.`}
                    </span>,
                    { type: 'warning', autoClose: false },
                );
            }
            onFlagged?.(reference);
            onClose();
        } catch (e: any) {
            const code: string | undefined = e?.response?.data?.message;
            const found: IraSearchHistoryTypes.PatientDetailKind[] = e?.response?.data?.result?.found ?? [];
            if (code === 'IRA_FLAG_COMMENT_HAS_PATIENT_DETAILS' && found.length) {
                setError(`Please remove ${listOf(found.map((kind) => DETAIL_LABELS[kind] ?? kind))}. Describe what looks wrong without patient details.`);
            } else {
                setError((code && RESPONSE_CODES[code]) || 'The report could not be sent to POM Support. Please try again.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle fontWeight={500} textAlign={'left'}>
                Report a problem to POM Support
            </DialogTitle>
            <DialogContent sx={{ mt: 1 }}>
                <Alert severity="info" icon={<SupportAgent />} sx={{ mb: 2 }}>
                    This goes to the POM Support team only. It is not shared with or assigned to anyone in your organization.
                </Alert>
                <DialogContentText textAlign={'left'} sx={{ mb: 2 }}>
                    Tell us what looks wrong with this result. POM Support can already open the search, so do not include
                    the patient's name, date of birth, address, or other personal details. You'll get a reference to quote
                    if you contact us about it.
                </DialogContentText>
                <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    autoFocus
                    value={comment}
                    disabled={isSaving}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="For example: the criminal records section lists charges from another state that don't belong to this person."
                    inputProps={{ maxLength: MAX_LENGTH, 'aria-label': 'What looks wrong' }}
                    helperText={`${trimmedLength}/${MAX_LENGTH}`}
                    FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
                />
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <Box sx={{ borderTop: 1, m: 1, p: 1, gap: 2, display: 'flex', borderColor: 'divider', flexDirection: 'row-reverse' }}>
                <LoadingButton
                    variant="alternate"
                    loading={isSaving}
                    disabled={!target || trimmedLength < MIN_LENGTH}
                    onClick={submit}
                    startIcon={<SupportAgent />}
                >
                    Send to POM Support
                </LoadingButton>
                <Button variant="outlined-alternate" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
            </Box>
        </Dialog>
    );
};
